import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Save } from "lucide-react";
import {
  useHomeContent,
  useUpdateHomeContent,
  HomeContent,
  DEFAULT_HOME_CONTENT,
} from "@/hooks/useHomeContent";

export const HomeContentEditor = () => {
  const { data, isLoading } = useHomeContent();
  const update = useUpdateHomeContent();
  const [form, setForm] = useState<HomeContent>(DEFAULT_HOME_CONTENT);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (isLoading) return <p>Loading…</p>;

  const setF = <K extends keyof HomeContent>(k: K, v: HomeContent[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const updateFeature = (i: number, key: "icon" | "title" | "text", val: string) => {
    const items = [...form.features_items];
    items[i] = { ...items[i], [key]: val };
    setF("features_items", items);
  };

  const addFeature = () =>
    setF("features_items", [...form.features_items, { icon: "Star", title: "", text: "" }]);
  const removeFeature = (i: number) =>
    setF("features_items", form.features_items.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-6">
      <Card className="p-6 space-y-4">
        <h3 className="text-lg font-semibold">Hero (left side)</h3>
        <div>
          <Label>Title</Label>
          <Input value={form.hero_left_title} onChange={(e) => setF("hero_left_title", e.target.value)} />
        </div>
        <div>
          <Label>Subtitle / badge</Label>
          <Input value={form.hero_left_subtitle} onChange={(e) => setF("hero_left_subtitle", e.target.value)} />
        </div>
        <div>
          <Label>Tagline</Label>
          <Textarea value={form.hero_left_tagline} onChange={(e) => setF("hero_left_tagline", e.target.value)} rows={2} />
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="text-lg font-semibold">Booking form</h3>
        <div>
          <Label>Title</Label>
          <Input value={form.booking_form_title} onChange={(e) => setF("booking_form_title", e.target.value)} />
        </div>
        <div>
          <Label>Subtitle</Label>
          <Input value={form.booking_form_subtitle} onChange={(e) => setF("booking_form_subtitle", e.target.value)} />
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Features</h3>
          <Button type="button" variant="outline" size="sm" onClick={addFeature}>
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
        <div>
          <Label>Section title</Label>
          <Input value={form.features_title} onChange={(e) => setF("features_title", e.target.value)} />
        </div>
        {form.features_items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end border-t pt-3">
            <div className="col-span-2">
              <Label>Icon</Label>
              <Input value={it.icon} onChange={(e) => updateFeature(i, "icon", e.target.value)} placeholder="Crown" />
            </div>
            <div className="col-span-3">
              <Label>Title</Label>
              <Input value={it.title} onChange={(e) => updateFeature(i, "title", e.target.value)} />
            </div>
            <div className="col-span-6">
              <Label>Text</Label>
              <Input value={it.text} onChange={(e) => updateFeature(i, "text", e.target.value)} />
            </div>
            <div className="col-span-1">
              <Button type="button" variant="ghost" size="icon" onClick={() => removeFeature(i)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="text-lg font-semibold">Call to Action (bottom)</h3>
        <div>
          <Label>Title</Label>
          <Input value={form.cta_title} onChange={(e) => setF("cta_title", e.target.value)} />
        </div>
        <div>
          <Label>Subtitle</Label>
          <Textarea value={form.cta_subtitle} onChange={(e) => setF("cta_subtitle", e.target.value)} rows={2} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Button label</Label>
            <Input value={form.cta_button_label} onChange={(e) => setF("cta_button_label", e.target.value)} />
          </div>
          <div>
            <Label>Button link</Label>
            <Input value={form.cta_button_link} onChange={(e) => setF("cta_button_link", e.target.value)} placeholder="/transfers" />
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => update.mutate(form)} disabled={update.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {update.isPending ? "Saving…" : "Save Home Content"}
        </Button>
      </div>
    </div>
  );
};

export default HomeContentEditor;
